import React from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  fetchTransferFleetVehicles,
  transferRecordQueryKeys,
  type TransferRecordShipment,
  type TransferShipmentInput,
} from '@/data/transferRecord';
import { CARRIER_SUGGESTIONS, SHIPMENT_MODES, fromSiteInputValue, siteZoneAbbreviation, toSiteInputValue } from './transferRecordView';

export type ShipmentDraft = {
  carrier: string;
  mode: string;
  trackingNo: string;
  vehicleId: string;
  driverName: string;
  /** datetime-local values on the shipping (left) and receiving (delivered) sites' clocks. */
  shippedAt: string;
  deliveredAt: string;
  notes: string;
};

export const EMPTY_SHIPMENT: ShipmentDraft = {
  carrier: '',
  mode: '',
  trackingNo: '',
  vehicleId: '',
  driverName: '',
  shippedAt: '',
  deliveredAt: '',
  notes: '',
};

export function shipmentDraftFrom(
  shipment: TransferRecordShipment,
  fromZone: string | null,
  toZone: string | null,
): ShipmentDraft {
  return {
    carrier: shipment.carrier ?? '',
    mode: shipment.mode ?? '',
    trackingNo: shipment.trackingNo ?? '',
    vehicleId: shipment.vehicle?.id ?? '',
    driverName: shipment.driverName ?? '',
    shippedAt: toSiteInputValue(shipment.shippedAt, fromZone),
    deliveredAt: toSiteInputValue(shipment.deliveredAt, toZone),
    notes: shipment.notes ?? '',
  };
}

/** The draft as the RPC's shipment object (times only when the form shows them). */
export function shipmentInputFrom(
  draft: ShipmentDraft,
  options: { withTimes: boolean; fromZone: string | null; toZone: string | null },
): TransferShipmentInput {
  const input: TransferShipmentInput = {
    carrier: draft.carrier.trim() || null,
    mode: draft.mode || null,
    // An empty string clears a saved tracking number; null would keep it.
    tracking_no: draft.trackingNo.trim(),
    vehicle_id: draft.vehicleId || null,
    driver_name: draft.driverName.trim() || null,
    notes: draft.notes.trim() || null,
  };
  if (options.withTimes) {
    input.shipped_at = draft.shippedAt ? fromSiteInputValue(draft.shippedAt, options.fromZone) : null;
    input.delivered_at = draft.deliveredAt ? fromSiteInputValue(draft.deliveredAt, options.toZone) : null;
  }
  return input;
}

export function shipmentDraftIsEmpty(draft: ShipmentDraft): boolean {
  return !draft.carrier.trim() && !draft.mode && !draft.trackingNo.trim() && !draft.vehicleId
    && !draft.driverName.trim() && !draft.notes.trim();
}

type TransferShipmentFieldsProps = {
  value: ShipmentDraft;
  onChange: (next: ShipmentDraft) => void;
  /** The shipping site, whose fleet the vehicle list shows. */
  fromSiteId: string | null;
  fromZone: string | null;
  toZone: string | null;
  /** Shows "Left" and "Delivered" (saving a shipment); shipping sets the time itself. */
  withTimes: boolean;
  disabled?: boolean;
};

/** How it travels: carrier or truck, method, tracking number, driver and notes. */
export default function TransferShipmentFields({
  value,
  onChange,
  fromSiteId,
  fromZone,
  toZone,
  withTimes,
  disabled = false,
}: TransferShipmentFieldsProps) {
  const ids = {
    carrier: React.useId(),
    carriers: React.useId(),
    mode: React.useId(),
    tracking: React.useId(),
    vehicle: React.useId(),
    driver: React.useId(),
    shipped: React.useId(),
    delivered: React.useId(),
    notes: React.useId(),
  };
  const vehiclesQuery = useQuery({
    queryKey: transferRecordQueryKeys.vehicles(fromSiteId ?? ''),
    queryFn: () => fetchTransferFleetVehicles(fromSiteId ?? ''),
    enabled: Boolean(fromSiteId),
    staleTime: 60_000,
  });
  const vehicles = vehiclesQuery.data ?? [];
  const fromAbbr = siteZoneAbbreviation(fromZone);
  const toAbbr = siteZoneAbbreviation(toZone);

  function update(patch: Partial<ShipmentDraft>) {
    onChange({ ...value, ...patch });
  }

  function handleCarrier(event: React.ChangeEvent<HTMLInputElement>) {
    update({ carrier: event.target.value });
  }

  function handleMode(event: React.ChangeEvent<HTMLSelectElement>) {
    update({ mode: event.target.value });
  }

  function handleTracking(event: React.ChangeEvent<HTMLInputElement>) {
    update({ trackingNo: event.target.value });
  }

  function handleVehicle(event: React.ChangeEvent<HTMLSelectElement>) {
    update({ vehicleId: event.target.value });
  }

  function handleDriver(event: React.ChangeEvent<HTMLInputElement>) {
    update({ driverName: event.target.value });
  }

  function handleShipped(event: React.ChangeEvent<HTMLInputElement>) {
    update({ shippedAt: event.target.value });
  }

  function handleDelivered(event: React.ChangeEvent<HTMLInputElement>) {
    update({ deliveredAt: event.target.value });
  }

  function handleNotes(event: React.ChangeEvent<HTMLTextAreaElement>) {
    update({ notes: event.target.value });
  }

  return (
    <fieldset className="transfer-record-fieldset" disabled={disabled}>
      <legend className="transfer-record-fieldset__legend">How it travels</legend>
      <div className="transfer-record-form-grid">
        <div className="transfer-record-field">
          <label className="transfer-record-field__label" htmlFor={ids.carrier}>Carrier</label>
          <input
            id={ids.carrier}
            className="transfer-record-field__control"
            list={ids.carriers}
            value={value.carrier}
            onChange={handleCarrier}
            placeholder="UPS, FedEx or ITA vehicle"
            maxLength={120}
          />
          <datalist id={ids.carriers}>
            {CARRIER_SUGGESTIONS.map((carrier) => <option key={carrier} value={carrier} />)}
          </datalist>
        </div>
        <div className="transfer-record-field">
          <label className="transfer-record-field__label" htmlFor={ids.mode}>Method</label>
          <select id={ids.mode} className="transfer-record-field__control" value={value.mode} onChange={handleMode}>
            <option value="">Not set</option>
            {SHIPMENT_MODES.map((mode) => <option key={mode.value} value={mode.value}>{mode.label}</option>)}
          </select>
        </div>
        <div className="transfer-record-field">
          <label className="transfer-record-field__label" htmlFor={ids.tracking}>Tracking number</label>
          <input
            id={ids.tracking}
            className="transfer-record-field__control"
            value={value.trackingNo}
            onChange={handleTracking}
            placeholder="Optional"
            autoComplete="off"
            spellCheck={false}
            maxLength={120}
          />
        </div>
        <div className="transfer-record-field">
          <label className="transfer-record-field__label" htmlFor={ids.vehicle}>Vehicle</label>
          <select
            id={ids.vehicle}
            className="transfer-record-field__control"
            value={value.vehicleId}
            onChange={handleVehicle}
            disabled={disabled || (!vehicles.length && !value.vehicleId)}
          >
            <option value="">{vehiclesQuery.isLoading ? 'Loading vehicles…' : vehicles.length ? 'No vehicle' : 'No vehicles at this site'}</option>
            {vehicles.map((vehicle) => (
              <option key={vehicle.id} value={vehicle.id}>
                {[vehicle.name, vehicle.plate].filter(Boolean).join(' · ')}
              </option>
            ))}
          </select>
        </div>
        <div className="transfer-record-field">
          <label className="transfer-record-field__label" htmlFor={ids.driver}>Driver</label>
          <input
            id={ids.driver}
            className="transfer-record-field__control"
            value={value.driverName}
            onChange={handleDriver}
            placeholder="Optional"
            maxLength={120}
          />
        </div>
        {withTimes ? (
          <>
            <div className="transfer-record-field">
              <label className="transfer-record-field__label" htmlFor={ids.shipped}>
                Left{fromAbbr ? ` (${fromAbbr})` : ''}
              </label>
              <input
                id={ids.shipped}
                className="transfer-record-field__control"
                type="datetime-local"
                value={value.shippedAt}
                onChange={handleShipped}
              />
            </div>
            <div className="transfer-record-field">
              <label className="transfer-record-field__label" htmlFor={ids.delivered}>
                Delivered{toAbbr ? ` (${toAbbr})` : ''}
              </label>
              <input
                id={ids.delivered}
                className="transfer-record-field__control"
                type="datetime-local"
                value={value.deliveredAt}
                onChange={handleDelivered}
              />
            </div>
          </>
        ) : null}
        <div className="transfer-record-field transfer-record-field--full">
          <label className="transfer-record-field__label" htmlFor={ids.notes}>Notes</label>
          <textarea
            id={ids.notes}
            className="transfer-record-field__control transfer-record-field__control--text"
            value={value.notes}
            onChange={handleNotes}
            rows={2}
            placeholder="e.g. Spare lamp kit, 1 box"
            maxLength={1000}
          />
        </div>
      </div>
    </fieldset>
  );
}
